import { useEffect, useState } from "react";
import { CHANGELOG, type ChangelogEntry } from "../changelog";

/**
 * Is there a newer build on the server than the one this tab is running.
 *
 * The VS Code arrangement, on purpose: a small button appears when an update
 * exists, and NOTHING ELSE HAPPENS until it is clicked. A page that reloads
 * itself mid-task drops whatever somebody was typing into an approval or a
 * floor, and the finance team does not read change logs -- they notice a
 * screen that changed under them. So the deploy is announced, and the person
 * decides when.
 *
 * HOW IT KNOWS. Every build bakes its own id into the bundle (`__BUILD_ID__`,
 * from vite.config.ts) and writes the same id to `/version.json` beside
 * index.html. The API serves that file `no-store`, so the tab asks for it and
 * gets the truth: the moment the two differ, a deploy has landed. The check is
 * a 30-byte GET with no login on it and nothing personal in it.
 *
 * WHEN IT ASKS. On mount, whenever the tab becomes visible again, and every
 * five minutes while it stays visible. A tab in the background asks nothing --
 * there is nobody there to tell -- and a check that fails (offline, or the
 * server mid-restart answering 503) is simply not an answer: it is ignored,
 * not shown. The flag latches: once a newer build has been seen it stays seen,
 * because a server can only move forward.
 *
 * Not in development. The Vite dev server has no version.json and serves
 * index.html for it, and the id there is whatever second the config loaded.
 */

const CHECK_EVERY_MS = 5 * 60_000;

// A popover is a glance, not a release note. Eight lines is already more than
// anyone reads standing at a screen; past that it is a wall, and the ones that
// matter are the newest, which are on top.
const MAX_SHOWN = 8;

/**
 * What the popover says when a deploy brought no line of its own (a docs-only
 * deploy, or a PR labelled no-changelog). It used to say nothing -- the icon
 * hid -- and on 2026-10-15 that meant a day of Update prompts with no reason
 * given. An Update button must never come without words. id 0 is below every
 * real entry, so it can never collide with or shadow one.
 */
export const NO_NOTE: ChangelogEntry = {
  id: 0,
  text: "Small behind-the-scenes fixes — nothing changes in how you use Orbit.",
};

/**
 * The comparison on its own, so the shape check has a test.
 *
 * `payload` is whatever the fetch parsed. It has to be an object carrying a
 * non-empty string `build` that differs from ours; anything else -- the HTML
 * page a dev server would answer with, a typo'd key, an empty stamp -- is
 * not evidence of a new build and answers false. Announcing an update off a
 * malformed reply would put a permanent "Reload" button on every screen for
 * no reason, and a button people learn to ignore is worse than none.
 */
export function newerBuildServed(current: string, payload: unknown): boolean {
  if (typeof payload !== "object" || payload === null) return false;
  const build = (payload as { build?: unknown }).build;
  return typeof build === "string" && build.length > 0 && build !== current;
}

/**
 * One entry is a plain `{ id: number, text: string }` and nothing else is.
 *
 * The list rides in version.json as data, so it is whatever JSON.parse made of
 * whatever the server sent -- a field could be missing, a type could be wrong,
 * an older build might carry no `changelog` key at all. Everything that is not
 * exactly a numbered line is dropped rather than rendered, because the one
 * thing worse than missing a "what's new" line is painting `undefined` into a
 * popover a finance team is looking at.
 */
function isEntry(value: unknown): value is ChangelogEntry {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as { id?: unknown; text?: unknown };
  return (
    typeof entry.id === "number" &&
    Number.isFinite(entry.id) &&
    typeof entry.text === "string" &&
    entry.text.length > 0
  );
}

/**
 * What is new to THIS tab, newest first, capped.
 *
 * `mine` is the list baked into the running bundle; its highest id is the
 * high-water mark of what this tab already knows about. `payload` is the parsed
 * version.json the server just handed out, carrying the NEW build's list. We
 * show only the lines above our own high-water mark -- the ones that genuinely
 * arrived since this tab loaded -- so two tabs on two different old builds each
 * see their own "since you" and not a generic list.
 *
 * ⚠️ THE PRE-FEATURE TAB. A tab from before any of this shipped has no list of
 * its own (`mine` is empty), so its high-water mark is -infinity and every line
 * the server sends counts as new. That is deliberate: the one time it happens,
 * "here is everything you have missed" is exactly right, and it only happens
 * once because the reloaded tab is then a current build with a full list.
 *
 * Pure and exported so the filter has a test. A false line here is a wrong
 * announcement on the one surface that is supposed to be trustworthy.
 */
export function newEntries(mine: ChangelogEntry[], payload: unknown): ChangelogEntry[] {
  if (typeof payload !== "object" || payload === null) return [];
  const served = (payload as { changelog?: unknown }).changelog;
  if (!Array.isArray(served)) return [];

  const highWater = mine.reduce((max, entry) => Math.max(max, entry.id), -Infinity);

  return served
    .filter(isEntry)
    .filter((entry) => entry.id > highWater)
    .sort((a, b) => b.id - a.id)
    .slice(0, MAX_SHOWN);
}

async function servedBuild(): Promise<unknown> {
  const response = await fetch("/version.json", {
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) return null;
  return response.json();
}

export interface UpdateState {
  /** A newer build is on the server. The flag latches once true. */
  available: boolean;
  /**
   * The user-facing lines new to this tab, newest first, capped. Empty until a
   * newer build is seen; once one is, never empty -- a build that announces
   * nothing gets NO_NOTE, so the Update button always comes with words.
   */
  whatsNew: ChangelogEntry[];
}

export function useUpdateAvailable(): UpdateState {
  const [state, setState] = useState<UpdateState>({ available: false, whatsNew: [] });

  useEffect(() => {
    // REVIEW-ONLY, dev builds only. The real check below never runs outside a
    // production build (the PROD gate on the next line), so on the dev preview
    // -- which is where the team reviews -- the button and its "what's new" are
    // invisible, which made this feature impossible to see without a deploy.
    // `?whatsnew=1` forces the prompt on, with the real changelog as its list,
    // so it can be looked at on orbit.localhost:5173 and on a phone. The whole
    // block is dead code in a production build (`import.meta.env.DEV` is
    // statically false there and the bundler drops it), so it cannot change
    // what a real user sees. Clicking Reload here just reloads the preview.
    if (import.meta.env.DEV && typeof window !== "undefined") {
      if (new URLSearchParams(window.location.search).has("whatsnew")) {
        setState({ available: true, whatsNew: CHANGELOG.slice(0, MAX_SHOWN) });
        return;
      }
    }

    if (!import.meta.env.PROD || state.available) return;

    let cancelled = false;
    let timer: number | null = null;

    async function check() {
      try {
        const payload = await servedBuild();
        if (!cancelled && newerBuildServed(__BUILD_ID__, payload)) {
          // Compute the "what's new" lines from the SAME payload that proved
          // the update, so the button and its note cannot disagree.
          const lines = newEntries(CHANGELOG, payload);
          setState({ available: true, whatsNew: lines.length > 0 ? lines : [NO_NOTE] });
        }
      } catch {
        // No answer is not an answer. Ask again next time.
      }
    }

    function start() {
      if (timer !== null) return;
      void check();
      timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    }
    function stop() {
      if (timer === null) return;
      window.clearInterval(timer);
      timer = null;
    }
    function onVisibility() {
      if (document.visibilityState === "visible") start();
      else stop();
    }

    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [state.available]);

  return state;
}

/**
 * Take the update. `location.reload()` is enough to be a hard refresh here:
 * the API serves index.html `no-store`, and every asset it names carries a
 * content hash in its filename, so the reloaded page cannot pick up a stale
 * bundle -- there is no cached copy of the new index to be stale from.
 */
export function applyUpdate(): void {
  window.location.reload();
}
